/**
 * In-flight async-turn store (Zustand, localStorage-persisted).
 *
 * `POST /sessions/{id}/messages` answers 202 with the user's message, and the
 * answer arrives later as turn.delta / turn.completed events on the session's
 * /events stream. This store records the in-flight turns so:
 *   - the Session page can render a streaming placeholder while one is pending;
 *   - a reload / backgrounded PWA resumes through the catch-up read of the
 *     messages after the turn's seq on the stream's `connected` frame (the
 *     live events were missed while away) — hence localStorage persistence;
 *   - a bubble can be badged with the agent that is producing it.
 *
 * Cleared on turn.completed / turn.failed, or by that catch-up read. Keyed by
 * turnKey; pendingTurnsForSession filters for the active session.
 *
 * THIS IS THE ONLY TURN STORE. There were briefly two: this one and a
 * `sessionState` "unified session state machine" that registered every turn a
 * SECOND time, from the same call site, and persisted it under the SAME
 * localStorage key (STORAGE_KEY_PENDING_TURNS). That was not a consolidation,
 * it was a duplicate, and it cost three ways:
 *   - `isMessagePending` was `hasPendingTurnForMessage` character for
 *     character, so MessageBubble subscribed to two stores and OR'd them —
 *     with a comment explaining why the OR could not be short-circuited;
 *   - one shared persist key meant whichever store wrote last clobbered the
 *     other's map in localStorage, so a turn registered only here (the
 *     onboarding welcome turn) disappeared from storage the moment the other
 *     store wrote, and a reload lost its "still thinking" state;
 *   - its `actions` map was a third copy of `stores/pendingActions`, and its
 *     `status` field a fourth notion of turn state beside the server's own
 *     `TurnStatusResponse.status` — nothing outside its unit test read either.
 * What that store did carry and this one lacked — agent attribution — now
 * lives on SessionTurn below. One store, one key, one selector per question.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

import type { AgentAttribution } from '@/api/types';
import { STORAGE_KEY_PENDING_TURNS } from '@/lib/constants';

export interface SessionTurn {
  /** The turn's key in this store (turnKey for a turn the client sent). */
  turnId: string;
  /** Session the turn belongs to (scopes the placeholder + catch-up). */
  sessionId: string;
  /**
   * The seq of the user's message the turn answers: the catch-up read starts
   * after it, and the SSE frames name it. useStreamingChat, the one
   * registrar, always sets it. It is optional because this store is
   * persisted: a turn stored before turns carried a seq (the retired
   * onboarding welcome turn among them) reloads without one, and the
   * catch-up on connect drops it and re-reads the detail instead.
   */
  seq?: number;
  /**
   * Pre-allocated assistant message id the deltas/answer reconcile against.
   * Optional for the same reason as `seq`: a turn persisted by an older
   * client may lack it, and hasPendingTurnForMessage then never matches it.
   */
  assistantMessageId?: string;
  /**
   * Which agent is producing this turn, when the caller knows before the
   * answer lands. MessageBubble badges the placeholder with it; once the
   * message is persisted, `MessageResponse.agent` carries the same shape and
   * takes over.
   */
  agent?: AgentAttribution;
  /** Epoch ms when accepted — for progressive "taking longer…" feedback. */
  startedAt: number;
}

/** turnKey is the store key of the turn answering message `seq` of a session. */
export function turnKey(sessionId: string, seq: number): string {
  return `seq:${sessionId}:${seq}`;
}

interface PendingTurnsState {
  /** turn_id → SessionTurn. */
  turns: Record<string, SessionTurn>;
  /** Record (or replace) an in-flight turn. `startedAt` defaults to now. */
  registerTurn: (turn: Omit<SessionTurn, 'startedAt'> & { startedAt?: number }) => void;
  /** Remove a turn once terminal (completed / failed). No-op if absent. */
  clearTurn: (turnId: string) => void;
  /** Drop ALL in-flight turns. Called at an auth identity boundary so one
   *  nurse's turns never resume under the next nurse on a shared device. */
  reset: () => void;
}

export const usePendingTurnsStore = create<PendingTurnsState>()(
  persist(
    (set) => ({
      turns: {},
      registerTurn: ({ startedAt, ...turn }) =>
        set((state) => ({
          turns: {
            ...state.turns,
            [turn.turnId]: { ...turn, startedAt: startedAt ?? Date.now() },
          },
        })),
      clearTurn: (turnId) =>
        set((state) => {
          if (!(turnId in state.turns)) {
            return state;
          }
          const next = { ...state.turns };
          delete next[turnId];
          return { turns: next };
        }),
      reset: () => set({ turns: {} }),
    }),
    {
      name: STORAGE_KEY_PENDING_TURNS,
      storage: createJSONStorage(() => localStorage),
      // Only the turns map is state worth persisting; actions are recreated.
      partialize: (state) => ({ turns: state.turns }),
    },
  ),
);

/**
 * pendingTurnsForSession returns the pending turns belonging to one session,
 * oldest first. A plain selector (not a hook) so a non-React caller (the SSE
 * handler) can read it via usePendingTurnsStore.getState().
 */
export function pendingTurnsForSession(
  turns: Record<string, SessionTurn>,
  sessionId: string,
): SessionTurn[] {
  return Object.values(turns)
    .filter((t) => t.sessionId === sessionId)
    .sort((a, b) => a.startedAt - b.startedAt);
}

/**
 * hasPendingTurnForMessage reports whether an assistant message id is the
 * pre-allocated placeholder of an in-flight async turn.
 *
 * The chat renderer uses this to tell an empty placeholder ("thinking", the
 * answer is still streaming on the worker) apart from a settled empty/failed
 * assistant message ("unable to generate a response"). On the 202 the server
 * returns the assistant id but persists no `messages` row until the worker
 * finishes, so for the in-flight window the bubble is empty AND its id 404s on
 * /feedback — both must NOT read as a generation failure. A plain selector so
 * MessageBubble passes it straight to usePendingTurnsStore.
 */
export function hasPendingTurnForMessage(
  turns: Record<string, SessionTurn>,
  assistantMessageId: string,
): boolean {
  for (const t of Object.values(turns)) {
    if (t.assistantMessageId === assistantMessageId) return true;
  }
  return false;
}

/**
 * agentForPendingMessage returns the agent attribution recorded for an
 * in-flight turn's placeholder bubble, or undefined when the turn is unknown
 * or carried no attribution. Same selector shape as the two above.
 */
export function agentForPendingMessage(
  turns: Record<string, SessionTurn>,
  assistantMessageId: string,
): AgentAttribution | undefined {
  for (const t of Object.values(turns)) {
    if (t.assistantMessageId === assistantMessageId) return t.agent;
  }
  return undefined;
}

/**
 * sessionIdsWithPendingTurn — the set of sessions currently running a turn.
 *
 * The SSE replay-overflow path needs this to decide which session details it
 * may refetch immediately: a session with an in-flight turn holds a
 * client-only placeholder that the server has not persisted, so a refetch
 * would delete the bubble the deltas are streaming into.
 */
export function sessionIdsWithPendingTurn(
  turns: Record<string, SessionTurn>,
): Set<string> {
  const ids = new Set<string>();
  for (const t of Object.values(turns)) {
    if (t.sessionId) ids.add(t.sessionId);
  }
  return ids;
}

// -- Exported for tests only --
/** @internal reset the store to its initial state. */
export function __resetPendingTurnsForTests(): void {
  usePendingTurnsStore.getState().reset();
}
